import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import { CloseModalIcon } from '~/assets/images';
import OlcTooltip from '~/components/General/OlcTooltip';

import './styles.scss';

const findNewContactOptions = [
  {
    icon: <MyLocationOutlinedIcon />,
    title: 'Radius Around an Address',
    description: 'Enter an address and find contacts within a radius around it.',
    state: { initialOrderOption: 'find_new', initialFindNewOption: 'addresses' },
  },
  {
    icon: <MapOutlinedIcon />,
    title: 'Search an Area',
    description: 'Pick or draw an area, then filter for the contacts you want.',
    state: { initialOrderOption: 'find_new', initialFindNewOption: 'locations' },
  },
];

const AddContactsGuideModal = ({
  findNewContactsDisabled = false,
  findNewContactsDisabledMessage = '',
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const [showFindNewOptions, setShowFindNewOptions] = useState(false);
  const shouldShowFindNewOptions = showFindNewOptions && !findNewContactsDisabled;

  const closeModal = () => {
    setShowFindNewOptions(false);
    onClose();
  };

  const continueTo = (path, state) => {
    closeModal();
    navigate(path, state ? { state } : undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      aria-labelledby="add-contacts-guide-title"
      className="addContactsGuideModal"
    >
      <DialogContent className="addContactsGuideModalContent">
        <Box className="modalHeader">
          <Box>
            <Typography id="add-contacts-guide-title" className="title">
              Add your contacts
            </Typography>
            <Typography className="subTitle">
              Choose whether to use a list you already have or build a new targeted audience.
            </Typography>
          </Box>
          <IconButton aria-label="Close Add your contacts modal" onClick={closeModal}>
            <img src={CloseModalIcon} alt="close" />
          </IconButton>
        </Box>

        <Box className="guideOptions">
          <button
            type="button"
            className="guideOption"
            onClick={() => continueTo('/contacts/import?origin=contacts')}
          >
            <span className="guideOptionIcon">
              <UploadFileOutlinedIcon />
            </span>
            <span className="guideOptionTitle">Import from a file</span>
            <span className="guideOptionDescription">
              Use an existing contact list and continue through the standard import flow.
            </span>
          </button>
          <OlcTooltip
            arrow
            disableInteractive={!findNewContactsDisabled}
            placement="top"
            title={findNewContactsDisabled ? findNewContactsDisabledMessage : ''}
          >
            <span className="guideOptionTooltipTarget">
              <button
                type="button"
                className={`guideOption ${shouldShowFindNewOptions ? 'selected' : ''} ${findNewContactsDisabled ? 'disabled' : ''}`}
                aria-disabled={findNewContactsDisabled}
                aria-expanded={shouldShowFindNewOptions}
                disabled={findNewContactsDisabled}
                onClick={() => {
                  if (findNewContactsDisabled) return;

                  setShowFindNewOptions(true);
                }}
              >
                <span className="guideOptionIcon">
                  <PersonSearchOutlinedIcon />
                </span>
                <span className="guideOptionTitle">Find new contacts</span>
                <span className="guideOptionDescription">
                  Build a fresh list by choosing the search method that fits your audience.
                </span>
              </button>
            </span>
          </OlcTooltip>
        </Box>

        {shouldShowFindNewOptions && (
          <Box className="guideSubOptionsPanel" aria-label="Find new contacts options">
            <Typography className="guideSubOptionsTitle">
              How do you want to find contacts?
            </Typography>
            <Box className="guideSubOptions">
              {findNewContactOptions.map((option) => (
                <button
                  type="button"
                  className="guideOption"
                  key={option.title}
                  onClick={() => continueTo('/orders/create-order', option.state)}
                >
                  <span className="guideOptionIcon">{option.icon}</span>
                  <span className="guideOptionTitle">{option.title}</span>
                  <span className="guideOptionDescription">{option.description}</span>
                </button>
              ))}
            </Box>
          </Box>
        )}

        <Box className="modalFooter">
          <Typography>You can add more contacts or build another audience anytime.</Typography>
          <Button variant="outlined" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddContactsGuideModal;
